import { useMenu } from '@happyspace/sdk/restaurant';
import { MENU, SIGNATURES, ITEM_COUNT, type MenuSection, type MenuItem, type Tag } from '../data/menu';

/* ------------------------------------------------------------------
   Live menu, the formal Happyspace way.

   Reads the restaurant's menu from the Happyspace Restaurant SDK
   (`useMenu()` → resolves window.SITE_CONFIG.businessId → Menu V2),
   and reshapes the flat SDK items into this site's curated section
   structure — grouping by category and preserving each section's
   kicker/blurb/order from the static MENU as metadata.

   If the SDK is still loading or returns nothing, it falls back to the
   bundled static menu so the site never renders empty.
   ------------------------------------------------------------------ */

// Dishes miscategorized as non-veg upstream (SDK tags/dietaryFlags) that are actually
// vegetarian — force their display tag to 'veg' regardless of what the SDK returns.
// Matched loosely (ignoring "and" / "&" / punctuation / extra spaces) so any wording
// variant of these two dishes ("...Sambar & Chutney", "...Sambar and Chutney",
// "...Sambar Chutney") is always caught.
const FORCE_VEG_PATTERNS: RegExp[] = [
  /^ghee dosa with sambar( and)? chutney$/,
  /^plain dosa with sambar( and)? chutney$/,
];

function looseName(name: string | undefined): string {
  return (name ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    // Dashboard spelling drift: "Aappam" / "Appam" / "Aapam" are all the same word
    // with an inconsistent number of a's/p's — collapse them to one canonical
    // spelling BEFORE the name+price dedupe key is built, otherwise a legacy
    // picture-less stub (e.g. "Appam with Chicken Salna (2 Pc)") survives as a
    // separate entry alongside the photographed record (e.g. "Aappam with
    // Chicken Salna(2Pc)") instead of being recognized as the same dish.
    .replace(/\ba+p+am\b/g, 'appam')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tagOf(item: any): Tag {
  const n = looseName(item?.title ?? item?.name);
  if (FORCE_VEG_PATTERNS.some((re) => re.test(n))) return 'veg';
  const t = (item?.tags || []).find((x: string) => x === 'veg' || x === 'egg' || x === 'nonveg');
  if (t) return t as Tag;
  return item?.dietaryFlags?.isVegetarian ? 'veg' : 'nonveg';
}

// Items hidden from the public menu regardless of what the SDK/static data returns.
// "Plain Aapam" (and the bare, no-qualifier "Plain Appam") are legacy/duplicate entries —
// only "Plain Appam ( No sides )" should remain on the live menu.
const HIDDEN_ITEM_NAMES = new Set([
  'plain aapam',
  'plain appam',
  // Removed by owner request — hide every spelling variant ("Appam"/"Aappam"/"Aapam")
  // of this dish outright, regardless of which duplicate record has the photo.
  'appam with chicken salna 2 pc',
  // Removed by owner request — both size variants of Irani Chai.
  'irani chai small',
  'irani chai large',
]);

// Specific name+price pairs hidden from the public menu — used when only one priced
// variant of a dish should disappear (e.g. the $1.99 Sides & Add-Ons portion of
// Chicken 65) without hiding other differently-priced items sharing the same name.
const HIDDEN_ITEM_NAME_PRICE_PAIRS = new Set(['chicken 65|1.99']);

// Loosely-normalized name+price pairs hidden from the public menu — matched against
// looseName() so spelling/spacing variants ("Thums Up" / "Thumsup" / "Thums-Up") at
// this exact price are all caught, e.g. removing the $3.25 Thums Up from Beverages.
const HIDDEN_LOOSE_NAME_PRICE_PAIRS = new Set(['thums up|3.25']);

function isHidden(name: string | undefined, price?: number): boolean {
  const n = (name ?? '').trim().toLowerCase();
  const loose = looseName(name);
  if (HIDDEN_ITEM_NAMES.has(n) || HIDDEN_ITEM_NAMES.has(loose)) return true;
  if (typeof price === 'number' && HIDDEN_ITEM_NAME_PRICE_PAIRS.has(`${n}|${price}`)) return true;
  if (typeof price === 'number' && HIDDEN_LOOSE_NAME_PRICE_PAIRS.has(`${loose}|${price}`)) return true;
  return false;
}

// Dish names whose picture-less duplicate must be deleted outright (not just hidden) —
// if the live SDK returns a record for one of these names with no image, it is dropped
// unconditionally, regardless of price, so it can never survive dedupe or shadow the
// photographed version of the same dish.
const NO_IMAGE_MUST_DELETE = new Set(['plain dosa with sambar & chutney', 'plain dosa with sambar and chutney']);

function isDeletedForMissingImage(name: string | undefined, hasImage: boolean): boolean {
  const n = (name ?? '').trim().toLowerCase();
  return NO_IMAGE_MUST_DELETE.has(n) && !hasImage;
}

// Active promo: 20% off every Biryani and Kothu Parotta item, by menu section id.
// Expires automatically at 12:00 AM (midnight) on 2026-05-13 — i.e. the deal runs through
// end of day 2026-05-12 and is gone the next morning. No manual cleanup needed.
const DEAL_SECTIONS: Record<string, number> = { biryani: 20, kothu: 20 };
const DEAL_EXPIRES_AT = new Date(2026, 4, 13, 0, 0, 0).getTime(); // May 13, 2026, 00:00:00 local time

function isDealActive(): boolean {
  return Date.now() < DEAL_EXPIRES_AT;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Apply a section-level deal to an item — discounts price, keeps the original for
   the struck-through display, and (crucially) discounts the carried `raw` payload
   too, since addItem() prefers `raw.basePrice` over the display price for live items. */
function applyDeal(item: MenuItem, pct: number): MenuItem {
  const discounted = round2(item.price * (1 - pct / 100));
  return {
    ...item,
    originalPrice: item.price,
    dealPercent: pct,
    price: discounted,
    raw: item.raw ? { ...item.raw, basePrice: discounted } : item.raw,
  };
}

function withSectionDeal(sectionId: string, items: MenuItem[]): MenuItem[] {
  if (!isDealActive()) return items;
  const pct = DEAL_SECTIONS[sectionId];
  if (!pct) return items;
  return items.map((it) => applyDeal(it, pct));
}

export interface MenuData {
  sections: MenuSection[];
  signatures: MenuItem[];
  itemCount: number;
  loading: boolean;
  live: boolean;
}

export function useMenuData(): MenuData {
  const sdk = useMenu() as { items?: any[]; loading?: boolean };
  const items = sdk?.items;

  // Fallback to the bundled menu while loading or if the SDK has no items yet.
  if (!items || items.length === 0) {
    const sections = MENU.map((s) => ({
      ...s,
      items: withSectionDeal(s.id, s.items.filter((i) => !isHidden(i.name, i.price))),
    }));
    const signatures = sections.flatMap((s) => s.items).filter((i) => i.signature);
    const itemCount = sections.reduce((n, s) => n + s.items.length, 0);
    return { sections, signatures, itemCount, loading: !!sdk?.loading, live: false };
  }

  const byCat = new Map<string, any[]>();
  for (const it of items) {
    const c = it?.category || 'Menu';
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c)!.push(it);
  }

  const sections: MenuSection[] = MENU.map((meta) => {
    const src = byCat.get(meta.title);
    if (!src || src.length === 0) return meta;
    // Static photos curated for this section, keyed by dish name — used as a fallback
    // when the live SDK item has no image of its own uploaded yet.
    const staticImgByName = new Map<string, string>();
    // Static, human-written descriptions keyed by dish name — used as a fallback
    // when the live SDK item has no description filled in on the dashboard yet.
    const staticDescByName = new Map<string, string>();
    const staticDisabledByName = new Map<string, boolean>();
    for (const si of meta.items) {
      if (si.img) staticImgByName.set(si.name.trim().toLowerCase(), si.img);
      if (si.desc) staticDescByName.set(si.name.trim().toLowerCase(), si.desc);
      if (si.disabled) staticDisabledByName.set(si.name.trim().toLowerCase(), true);
    }
    const seen = new Set<string>();
    const mapped: MenuItem[] = src
      .slice()
      .sort((a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
      .filter((it: any) => {
        // Dedupe items the live menu returns more than once. The SDK can hand back
        // genuinely separate records (different _id) that are still the same dish
        // (same name + price) — so always key on name+price, never on _id alone.
        // When duplicates exist, prefer the richer record (has modifier sets and/or
        // an uploaded image) so a legacy stub record never silently shadows the
        // dashboard's current version and drops its modifiers (e.g. Spice Levels).
        if (isHidden(it?.title, it?.basePrice)) return false;
        // Unconditional delete: a picture-less record for a NO_IMAGE_MUST_DELETE dish is
        // dropped outright here, before dedupe/key logic runs — so it can never survive by
        // having a different price than its imaged twin, and never gets picked as the
        // "seen" representative for its name+price key.
        if (isDeletedForMissingImage(it?.title, !!it?.image)) return false;
        // Loosely normalized (spacing/punctuation-insensitive) so dashboard formatting
        // variants of the same dish name — e.g. "(2pc)" vs "(2 Pc)" — are recognized as
        // duplicates of each other instead of two separate menu entries.
        const key = `${looseName(it?.title)}|${it?.basePrice}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((it: any) => {
        // Resolve to the richest duplicate for this name+price before mapping.
        const key = `${looseName(it?.title)}|${it?.basePrice}`;
        const dupes = src.filter(
          (o: any) => `${looseName(o?.title)}|${o?.basePrice}` === key
        );
        if (dupes.length > 1) {
          // Prefer whichever duplicate actually has a photo — a record with modifiers
          // but no image should never win over a plain record that has one (this is
          // exactly the "Plain Dosa with Sambar & Chutney" case: two records, same
          // name+price, only one photographed — the photographed one must survive).
          const richest = dupes.find((o: any) => Array.isArray(o?.modifierSets) && o.modifierSets.length > 0 && o?.image);
          const withImage = dupes.find((o: any) => !!o?.image);
          const withMods = dupes.find((o: any) => Array.isArray(o?.modifierSets) && o.modifierSets.length > 0);
          return richest || withImage || withMods || it;
        }
        return it;
      })
      .map((it: any) => ({
        name: it?.title ?? '',
        price: typeof it?.basePrice === 'number' ? it.basePrice : 0,
        tag: tagOf(it),
        signature: !!it?.featured || (it?.tags || []).includes('signature'),
        desc: it?.description || staticDescByName.get((it?.title ?? '').trim().toLowerCase()) || undefined,
        weekend: (it?.tags || []).includes('weekend'),
        disabled: it?.disabled || it?.inStock === false || it?.available === false || staticDisabledByName.get((it?.title ?? '').trim().toLowerCase()) || false,
        raw: it,
        img: staticImgByName.get((it?.title ?? '').trim().toLowerCase()),
      }));
    return { ...meta, items: withSectionDeal(meta.id, mapped) };
  });

  const signatures = sections.flatMap((s) => s.items).filter((i) => i.signature);
  const itemCount = sections.reduce((n, s) => n + s.items.length, 0);
  return { sections, signatures, itemCount, loading: false, live: true };
}
